import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import { formatCapturedLabel } from "./guideViewModel";

export default function GuideStepList({
  steps,
  currentStepIndex,
  viewingStepIndex,
  maxViewIndex,
  stepValues,
  isTerminal,
  onSelect,
  renderActiveStep,
  palette,
  activeStepLabel = "Current",
}) {
  const rowRefs = useRef([]);

  useEffect(() => {
    const row = rowRefs.current[viewingStepIndex];
    if (!row) return;
    row.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [viewingStepIndex]);

  if (!steps.length) return null;

  return (
    <Box
      sx={{
        mb: 1.5,
        border: `1px solid ${palette.border}`,
        borderRadius: "8px",
        backgroundColor: palette.surface,
        p: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      {steps.map((step, idx) => {
        const captured = stepValues?.[step.stepId];
        const done =
          isTerminal && captured != null && String(captured).trim().length > 0
            ? true
            : idx <= currentStepIndex;
        const active = idx === viewingStepIndex;
        const reachable = isTerminal ? idx < steps.length : idx <= maxViewIndex;
        const label = step.label || `Step ${idx + 1}`;

        return (
          <Box
            key={step.stepId || idx}
            ref={(el) => {
              rowRefs.current[idx] = el;
            }}
            sx={{
              border: `1px solid ${active ? palette.selectedBorder : palette.border}`,
              borderRadius: "8px",
              backgroundColor: active ? palette.selectedBg : "transparent",
              opacity: !reachable && !isTerminal ? 0.5 : 1,
            }}
          >
            <Box
              role={reachable ? "button" : undefined}
              tabIndex={reachable ? 0 : -1}
              onClick={reachable ? () => onSelect(idx) : undefined}
              onKeyDown={
                reachable
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onSelect(idx);
                      }
                    }
                  : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 1,
                cursor: reachable ? "pointer" : "default",
                transition: "background-color 0.12s",
                "&:hover": reachable
                  ? {
                      backgroundColor: active
                        ? palette.selectedBg
                        : palette.fieldHoverBg,
                    }
                  : undefined,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "4px",
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "10px",
                  fontWeight: 700,
                  color: done || active ? palette.surface : palette.textSecondary,
                  backgroundColor: done || active ? palette.accent : palette.chipBg,
                  border: `1px solid ${
                    done || active ? palette.accent : palette.chipBorder
                  }`,
                }}
              >
                {done ? (
                  <CheckRoundedIcon sx={{ fontSize: 12, color: "inherit" }} />
                ) : (
                  idx + 1
                )}
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: "12px",
                  fontWeight: active ? 600 : 500,
                  color: active ? palette.textPrimary : palette.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                }}
              >
                {label}
              </Typography>
              {active && (
                <Typography
                  sx={{
                    flexShrink: 0,
                    fontSize: "10px",
                    fontWeight: 600,
                    color: palette.textSecondary,
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                  }}
                >
                  {activeStepLabel}
                </Typography>
              )}
            </Box>

            {active && renderActiveStep && (
              <Box
                sx={{ px: 1.25, pb: 1.25, pt: 0.25 }}
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {renderActiveStep(step, idx)}
              </Box>
            )}

            {!active && done && captured && (
              <Typography
                sx={{
                  px: 1.25,
                  pb: 1,
                  pt: 0,
                  fontSize: "11px",
                  color: palette.textSecondary,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {formatCapturedLabel(step, captured)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
